import { useState, useEffect } from "react";
import { useAuth, useTheme } from "../../global-components/src";
import { useMessage } from "../../global-components/src";
import axios from "axios";
import Screen from "../Layouts/Screen/Screen";
import { Clipboard, Eye, EyeOff } from "react-feather";
import { PACOCA_API_URL } from "../../global-components/src/utils/vars";
import { formatCurrency } from "../../global-components/src/utils/numbers";
import { getTypePayment, isPositive } from "../../global-components/src/utils/payment";
import { Banknote, BanknoteArrowDown, BanknoteArrowUpIcon, CirclePlus } from "lucide-react";
import { Icon } from "../../global-components/src/components/icon";
import { useNavigate } from "react-router-dom";
import "./style.css"
import { useTranslation } from 'react-i18next';

interface Transaction {
    id: number;
    amount: number;
    status: string;
    gateway: string;
    created_at: string;
    type: string;
    copyPaste?: string;
}

const WalletScreen = () => {
    const { token } = useAuth();
    const { setMessage } = useMessage();
    const { t } = useTranslation();
    const [balance, setBalance] = useState<number>(0);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [loading, setLoading] = useState(false);
    const [copiadoId, setCopiadoId] = useState<number | null>(null);
    const [hideBalance, setHideBalance] = useState(false);
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    const fetchWalletData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${PACOCA_API_URL}/wallet`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBalance(Number(response.data.balance));
            setTransactions(response.data.transactions);
        } catch (err: any) {
            setMessage(err?.response?.data?.message || "Erro ao carregar carteira", "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWalletData();
    }, []);

    const copyCode = async (tx: Transaction) => {
        if (!tx.copyPaste) return;
        try {
            await navigator.clipboard.writeText(tx.copyPaste);
            setCopiadoId(tx.id);
            setTimeout(() => setCopiadoId(null), 2000);
            setMessage(t("payment.copied"), "success");
        } catch (err) {
            console.error(err);
        }
    };

    const renderTxIcon = (tx: Transaction) => {
        if (tx.status !== "completed") {
            return (
                <span className="bank-tx-icon pending">
                    <Banknote size={17} />
                </span>
            );
        }
        if (isPositive(tx.type)) {
            return (
                <span className="bank-tx-icon credit">
                    <BanknoteArrowUpIcon size={17} />
                </span>
            );
        }
        return (
            <span className="bank-tx-icon debit">
                <BanknoteArrowDown size={17} />
            </span>
        );
    };

    return (
        <Screen
            title="Minha Carteira"
            contentMenu={
                <span className="cursor-pointer" onClick={() => navigate("/carteira/deposito")}>
                    <Icon size={24} style={{ margin: 0, cursor: "pointer" }} invertColor={!isDarkMode} name="add_card">
                        <CirclePlus opacity={0.6} size={23} />
                    </Icon>
                </span>
            }
        >
            <div className="padding-big-screen bank-wallet">
                <div className="bank-balance-card">
                    <div className="bank-balance-top">
                        <span className="bank-balance-label">Saldo de Paçoquinhas</span>
                        <button
                            type="button"
                            className="bank-balance-eye cursor-pointer"
                            onClick={() => setHideBalance(!hideBalance)}
                            aria-label={hideBalance ? "Mostrar saldo" : "Ocultar saldo"}
                        >
                            {hideBalance ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>

                    <div className="bank-balance-amount">
                        {loading ? (
                            <span className="placeholder" />
                        ) : hideBalance ? (
                            "R$ ••••"
                        ) : (
                            `R$ ${balance.toFixed(2).replace(".", ",")}`
                        )}
                    </div>

                    <div className="bank-balance-actions">
                        <button
                            type="button"
                            className="bank-balance-action-btn"
                            onClick={() => navigate("/carteira/deposito")}
                        >
                            <CirclePlus size={20} />
                            Adicionar
                        </button>
                    </div>
                </div>

                <h5 className="bank-tx-section-title">Transações Recentes</h5>

                {!loading && transactions.length === 0 && (
                    <div className="bank-tx-empty">Nenhuma transação encontrada.</div>
                )}

                {!loading && transactions.length > 0 && (
                    <div className="bank-tx-list">
                        {transactions.map((tx) => (
                            <div key={tx.id} className="bank-tx-item">
                                {renderTxIcon(tx)}
                                <div className="bank-tx-info">
                                    <div className="bank-tx-title">{getTypePayment(tx.type)}</div>
                                    <div className="bank-tx-date">
                                        {new Date(tx.created_at).toLocaleString()}
                                    </div>
                                </div>

                                {tx.copyPaste ? (
                                    <button
                                        type="button"
                                        className="bank-tx-copy-btn"
                                        onClick={() => copyCode(tx)}
                                    >
                                        <Clipboard size={14} />
                                        {copiadoId === tx.id ? "Copiado!" : "Copiar"}
                                    </button>
                                ) : (
                                    <span
                                        className={`bank-tx-amount ${
                                            tx.status === "completed" && isPositive(tx.type) ? "credit" : "debit"
                                        }`}
                                    >
                                        {tx.status === "completed" && isPositive(tx.type) ? "+ " : "- "}
                                        {formatCurrency(tx.amount.toString())}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {loading && (
                    <div className="bank-tx-list">
                        {[...Array(5)].map((_, index) => (
                            <div key={index} className="bank-tx-item">
                                <span className="bank-tx-skel-icon" />
                                <div className="bank-tx-info">
                                    <div className="bank-tx-skel-line" style={{ width: "60%", marginBottom: 6 }} />
                                    <div className="bank-tx-skel-line" style={{ width: "40%" }} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Screen>
    );
};

export default WalletScreen;